import { z } from 'zod';

/**
 * Mirror of the API FeatureCatalog. Labels are used by the tenant console when
 * the server catalog is not available yet; the server remains the authority on
 * which keys a plan or agency has.
 */
export const FEATURE_CATALOG = [
  { key: 'contracts', label: 'Contracts & handovers', description: 'Create rental contracts, hand over vehicles and collect signatures.', group: 'Operations' },
  { key: 'tariffs', label: 'Tariffs & extras', description: 'Edit seasonal rates, discount tiers and paid extras.', group: 'Operations' },
  { key: 'cash_register', label: 'Cash register & expenses', description: 'Daily cash journal, expenses and payment reconciliation.', group: 'Operations' },
  { key: 'fines', label: 'Traffic fines', description: 'Record contraventions and recover them from renters.', group: 'Operations' },
  { key: 'reports', label: 'Reports & revenue', description: 'Revenue analytics, fleet performance and profit reporting.', group: 'Insights' },
  { key: 'staff_roles', label: 'Staff accounts & roles', description: 'Invite staff and assign desk roles and permissions.', group: 'Team' },
  { key: 'multi_branch', label: 'Multiple branches', description: 'Run several pickup locations under one agency account.', group: 'Growth' },
  { key: 'public_site', label: 'Public website', description: 'Publish the agency storefront and online booking funnel.', group: 'Growth' },
  { key: 'custom_domain', label: 'Custom domain', description: 'Serve the storefront on the agency own domain.', group: 'Growth' },
  { key: 'qualified_signature', label: 'Qualified signature', description: 'Use the qualified electronic signature provider.', group: 'Platform' },
  { key: 'priority_support', label: 'Priority support', description: 'Priority queue and dedicated onboarding.', group: 'Platform' },
] as const;

export type FeatureKey = (typeof FEATURE_CATALOG)[number]['key'];

export interface FeatureMeta {
  key: string;
  label: string;
  description: string;
  group: string;
}

export const FEATURE_LABELS: Record<string, string> = Object.fromEntries(
  FEATURE_CATALOG.map((feature) => [feature.key, feature.label]),
);

const featureSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  group: z.string(),
});

export const entitlementsSchema = z.object({
  slug: z.string(),
  plan: z.object({ id: z.string(), name: z.string() }),
  planKeys: z.array(z.string()),
  extraKeys: z.array(z.string()),
  effective: z.array(z.string()),
  limits: z.object({
    vehicles: z.number().int().nullable(),
    staff: z.number().int().nullable(),
  }),
  catalog: z.array(featureSchema),
});

export type Entitlements = z.infer<typeof entitlementsSchema>;

export function parseEntitlements(payload: unknown): Entitlements {
  const parsed = entitlementsSchema.safeParse(payload);
  if (!parsed.success) throw new Error('The agency entitlements could not be read.');
  return parsed.data;
}

/** Desk sections that are gated by a plan feature. Everything else is core. */
export const SECTION_FEATURE: Partial<Record<string, FeatureKey>> = {
  handover: 'contracts',
  tarifs: 'tariffs',
  caisse: 'cash_register',
  amendes: 'fines',
  analytics: 'reports',
  staff: 'staff_roles',
};

export function featureForSection(section: string): FeatureKey | null {
  return SECTION_FEATURE[section] ?? null;
}

/**
 * Fails open when the entitlement payload is missing: a network hiccup must
 * never lock an agency out of its own desk. The API still enforces access.
 */
export function can(entitlements: Entitlements | null | undefined, feature: string): boolean {
  if (!entitlements) return true;
  return entitlements.effective.includes(feature);
}

export function featureLabel(
  feature: string,
  catalog: readonly { key: string; label: string }[] = FEATURE_CATALOG,
): string {
  return catalog.find((entry) => entry.key === feature)?.label ?? FEATURE_LABELS[feature] ?? feature;
}

export function groupFeatures(features: readonly FeatureMeta[]): { group: string; features: FeatureMeta[] }[] {
  const groups: { group: string; features: FeatureMeta[] }[] = [];
  for (const feature of features) {
    const existing = groups.find((entry) => entry.group === feature.group);
    if (existing) existing.features.push(feature);
    else groups.push({ group: feature.group, features: [feature] });
  }
  return groups;
}
